import { feedBoundaryMarker, feedLoading } from '../ui/32-learning.mjs';
import { activateFeedSurface } from '../ui/27-feed-activation.mjs';
import { observeContinuation } from '../ui/26-scroll-continuation.mjs';
import { createFeedReconciliation } from '../ui/22-feed-reconciliation.mjs';
import { createFeedWarming } from '../ui/21-feed-warming.mjs';
import { createRecommendations } from '../ui/18-recommendations.mjs';
import { FeedContinuity, FeedWarmup } from '../03-feed-session.mjs';
import { createStoryViewer } from '../ui/07-story-viewer.mjs';
// Owns feed view rendering and interactions.
// Dependencies are injected by app.mjs; feature modules never import one another.
/** Create feed views and paging interactions using the injected feed session and policies. */
export function createFeedView(ctx) {
  const recommendations = createRecommendations(ctx);
  const continuity = new FeedContinuity();
  let selection = 0;
  const warming = createFeedWarming(ctx);
  const warmup = new FeedWarmup(
    ({ mode, lane, format, cursor, limit, signal, contentScope }) =>
      ctx.api(
        `/api/feed?mode=${mode}&scope=${lane}&format=${format}&contentScope=${contentScope}&limit=${limit}${cursor ? '&cursor=' + encodeURIComponent(cursor) : ''}`,
        undefined,
        { signal },
      ),
    { canWarm: warming.canWarm, onPublicHints: warming.hints },
  );
  function disposeFeedWarmup() {
    selection++;
    warmup.clear();
    warming.dispose();
    disposeGameCards();
  }
  function alignWarmup() {
    warmup.align({
      actor: ctx.state.me.id,
      mode: ctx.mode,
      lane: ctx.feedScope,
      contentScope: ctx.contentScope ?? 'ordinary',
      discoveryPolicyVersion: ctx.discoveryPolicyVersion,
      learning: ctx.state.settings?.learning === true,
      epoch: ctx.state.settings?.interestEpoch ?? 0,
    });
  }
  const {
    paint: paintFeedItems,
    canReuse: canReuseFeedSurface,
    mount: mountSurface,
    dispose: disposeGameCards,
  } = createFeedReconciliation(ctx, caughtUpMarker);
  const alignContinuity = () =>
    continuity.align(
      ctx.state.me.id,
      ctx.feedScope,
      ctx.mode,
      ctx.state.settings?.learning,
      ctx.state.settings?.interestEpoch,
      ctx.contentScope,
      ctx.discoveryPolicyVersion,
    );
  function modeDescription() {
    if (ctx.mode === 'latest')
      return 'Newest updates first. Your emotional preferences do not change this order.';
    if (ctx.mode !== 'automatic')
      return `A ${ctx.mode} mix, with room for friends and new perspectives.`;
    if (!ctx.state.settings.learning)
      return 'Adaptation is paused. Your interests and follows still help order the feed.';
    return ctx.automaticTarget(ctx.state.settings.preference, Date.now()) !== ctx.BALANCED
      ? 'Gently adapting from your feedback. Refresh when you’re ready.'
      : 'Your interests and follows shape the feed. Three explicit tone signals can shape your next refresh.';
  }

  function selectOrder(value) {
    if (ctx.mode === value) return;
    ctx.mode = value;
    ctx.feed.reset(value);
    ctx.feedScroll = 0;
    ctx.render();
    if (['feed', 'learning'].includes(ctx.route()[0]))
      window.scrollTo({ top: 0, behavior: 'instant' });
  }

  function controls(close = () => {}) {
    const choices = ctx.e('div', 'preference-choices');
    choices.setAttribute('role', 'group');
    choices.setAttribute('aria-label', ctx.uiText('Feed preferences'));
    const icons = {
      automatic: 'sparkles',
      calm: 'leaf',
      connected: 'users',
      inspired: 'star',
      energized: 'zap',
      latest: 'clock',
    };
    for (const [value, label] of Object.entries(ctx.labels)) {
      if (value !== 'latest' && !ctx.featureEnabled('adaptive-feed')) continue;
      const choice = ctx.iconButton(icons[value], label, () => {
        close();
        selectOrder(value);
      });
      choice.classList.add('palette-choice');
      choice.append(ctx.e('span', 'palette-label', ctx.uiText(label)));
      choice.dataset.mode = value;
      choice.setAttribute('aria-pressed', String(ctx.mode === value));
      choices.append(choice);
    }
    return choices;
  }

  function preferencesPalette(trigger) {
    let close = () => {};
    const body = ctx.append(
      ctx.e('div', 'preference-palette'),
      controls(() => close()),
    );
    const tools = ctx.e('div', 'palette-tools');
    if (['feed', 'learning'].includes(ctx.route()[0]))
      tools.append(
        ctx.iconButton('refresh-cw', 'Refresh feed', async () => {
          close();
          await ctx.refreshFeed();
        }),
      );
    const learning = ctx.iconButton(
      ctx.state.settings.learning ? 'pause' : 'play',
      ctx.state.settings.learning ? 'Pause adaptation' : 'Resume adaptation',
      async () => {
        await ctx.command(
          { type: 'settings', learning: !ctx.state.settings.learning },
          { paint: false },
        );
        close();
        updatePreferences();
        ctx.toast(ctx.state.settings.learning ? 'Adaptation resumed.' : 'Adaptation is paused.');
      },
    );
    learning.setAttribute('aria-pressed', String(!ctx.state.settings.learning));
    learning.dataset.preferenceAction = 'learning';
    tools.append(
      learning,
      ctx.iconButton('rotate-ccw', 'Reset adaptation', async () => {
        await ctx.command({ type: 'reset-preference' }, { paint: false });
        close();
        updatePreferences();
        ctx.toast(ctx.translate('feed.learningResetNote'));
      }),
    );
    if (['feed', 'learning'].includes(ctx.route()[0]) && ctx.state.settings.reading?.anchor)
      tools.append(
        ctx.iconButton('bookmark', 'Resume last position', async () => {
          close();
          await resumeReading();
        }),
      );
    body.append(tools, ctx.e('p', 'palette-description', modeDescription()));
    close = ctx.contextMenu(trigger, 'Feed preferences', body);
  }

  let preferenceTrigger;
  function headerPreferences() {
    if (!preferenceTrigger) {
      preferenceTrigger = ctx.button('', preferencesPalette, 'order-button header-preferences');
      preferenceTrigger.dataset.focusKey = 'feed-preferences';
      preferenceTrigger.setAttribute('aria-haspopup', 'dialog');
      preferenceTrigger.setAttribute('aria-expanded', 'false');
    }
    updatePreferences();
    return preferenceTrigger;
  }
  function updatePreferences() {
    const trigger = preferenceTrigger;
    if (!trigger) return;
    const modeLabel = ctx.uiText(ctx.labels[ctx.mode]);
    const label = modeLabel + (ctx.state.settings.learning ? '' : ' · ' + ctx.uiText('Paused'));
    trigger.dataset.learningPaused = String(!ctx.state.settings.learning);
    trigger.replaceChildren(
      ctx.icon(
        {
          automatic: 'sparkles',
          calm: 'leaf',
          connected: 'users',
          inspired: 'star',
          energized: 'zap',
          latest: 'clock',
        }[ctx.mode],
      ),
      ctx.e('span', '', modeLabel),
    );
    trigger.setAttribute('aria-label', ctx.uiText('Feed preferences') + ': ' + label);
    trigger.title = modeDescription();
  }

  function stories() {
    const row = ctx.e('div', 'stories');
    const create = ctx.button('', () => ctx.composer('story'), 'story-button');
    create.setAttribute('aria-label', 'Create your story');
    create.append(
      ctx.append(ctx.e('span', 'story-add'), ctx.icon('plus')),
      ctx.e('span', 'story-name', 'Your story'),
    );
    row.append(create);
    for (const story of ctx.state.stories.filter(
      (s) =>
        ctx.feedScope === 'all' ||
        s.authorId === ctx.state.me.id ||
        (ctx.feedScope === 'friends' ? ctx.state.friends : ctx.state.following).includes(
          s.authorId,
        ),
    )) {
      const user = ctx.who(story.authorId);
      const b = ctx.button('', () => storyDialog(story.id), 'story-button');
      b.setAttribute('aria-label', `View ${user.name}’s story`);
      const img = ctx.field('img', {
        src: ctx.photoSource(story.image, 160),
        alt: '',
        loading: 'lazy',
        className: 'story-cover',
      });
      b.append(img, ctx.e('span', 'story-name', user.name.split(' ')[0]));
      row.append(b);
    }
    return row;
  }

  let storyViewer;
  function storyDialog(id) {
    storyViewer?.dispose();
    const sequence = ctx.state.stories.filter(
      (story) =>
        (!story.expiresAt || story.expiresAt > Date.now()) &&
        (ctx.feedScope === 'all' ||
          story.authorId === ctx.state.me.id ||
          (ctx.feedScope === 'friends' ? ctx.state.friends : ctx.state.following).includes(
            story.authorId,
          )),
    );
    if (!sequence.some((story) => story.id === id)) return;
    storyViewer = createStoryViewer(ctx, sequence, id);
  }

  async function chooseExperience(value) {
    selection++;
    if (ctx.switchingExperience) return;
    const flag = {
      visual: 'visual-feed',
      talk: 'talk-feed',
      circles: 'circles',
      games: 'games',
    }[value];
    if (flag && ctx.state.features?.[flag] === false) {
      ctx.toast('This experience is unavailable on this server.');
      return;
    }
    alignContinuity();
    if (['feed', 'learning'].includes(ctx.route()[0]))
      continuity.save(ctx.experience, ctx.feed, window.scrollY);
    if (value === 'circles' || value === 'games') {
      selection++;
      ctx.go(value);
      return;
    }
    if (value === ctx.experience && ['feed', 'learning'].includes(ctx.route()[0])) return;
    const origin = ctx.route().join('/');
    const actor = ctx.state.me.id;
    const saved = continuity.views.get(value);
    if (ctx.api && ctx.feed.adopt && (!saved || saved.items.length <= 48))
      return activateFeedSurface(ctx, {
        value,
        origin,
        actor,
        intent: ++selection,
        selected: (intent) => intent === selection,
        warmup,
        alignWarmup,
        paintFeedItems,
        setupPaging,
        saved,
      });
    const intent = ++selection;
    const order = ctx.mode,
      lane = ctx.feedScope,
      contentScope = ctx.contentScope;
    const active = () =>
      intent === selection &&
      ctx.state?.me.id === actor &&
      ctx.route().join('/') === origin &&
      ctx.mode === order &&
      ctx.feedScope === lane &&
      ctx.contentScope === contentScope;
    ctx.switchingExperience = true;
    try {
      await ctx.flushSeen();
      if (!active()) return;
      await ctx.command(
        { type: 'settings', experience: value },
        { paint: false, fullSnapshot: true },
      );
      // A delayed preference response must not take over a newer actor or navigation.
      if (!active()) return;
      ctx.experience = value;
      ctx.feedScroll = continuity.restore(value, ctx.feed, ctx.mode);
      const alreadyInFeed = ['feed', 'learning'].includes(ctx.route()[0]);
      ctx.go(ctx.contentScope === 'learning' ? 'learning' : 'feed');
      if (alreadyInFeed) window.scrollTo({ top: ctx.feedScroll, behavior: 'instant' });
    } catch (error) {
      ctx.toast(error.message);
    } finally {
      ctx.switchingExperience = false;
    }
  }

  function modeWheel() {
    return ctx.mountModeWheel(
      ['circles', 'games'].includes(ctx.route()[0]) ? ctx.route()[0] : ctx.experience,
      {
        wall: ctx.uiText('Wall'),
        visual: ctx.uiText('Visual'),
        talk: ctx.uiText('Talk'),
        circles: ctx.uiText('Circles'),
        games: ctx.uiText('Games'),
      },
      chooseExperience,
      [...ctx.EXPERIENCES, 'games']
        .filter((value) => value !== 'visual' || ctx.state.features?.['visual-feed'] !== false)
        .filter((value) => value !== 'circles' || ctx.state.features?.circles !== false)
        .filter((value) => value !== 'talk' || ctx.state.features?.['talk-feed'] !== false)
        .filter((value) => value !== 'games' || ctx.state.features?.games === true),
      (value) =>
        ctx.icon(
          {
            wall: 'house',
            visual: 'image',
            talk: 'message-circle',
            circles: 'users',
            games: 'grid-2x2',
          }[value],
        ),
    );
  }

  function renderFeed() {
    recommendations.dispose();
    if (canReuseFeedSurface()) {
      paintFeedItems();
      if (ctx.feed.started) setupPaging();
      else {
        document.querySelector('#feed-list')?.append(feedLoading(ctx));
        loadNext();
      }
      return;
    }
    mountSurface();
    ctx.renderLearningHeader?.(ctx.view);
    const pullIndicator = ctx.e('div', 'pull-refresh');
    pullIndicator.id = 'pull-refresh';
    pullIndicator.hidden = true;
    pullIndicator.setAttribute('role', 'status');
    const list = ctx.e('div');
    list.id = 'feed-list';
    const sentinel = ctx.e('div', 'feed-sentinel');
    sentinel.id = 'feed-sentinel';
    const lanes = ctx.e('div', 'feed-lanes');
    lanes.setAttribute('role', 'group');
    lanes.setAttribute('aria-label', 'Feed lane');
    for (const [value, label, symbol] of [
      ['all', 'For you', 'sparkles'],
      ['friends', 'Friends', 'users'],
      ['following', 'Following', 'user-plus'],
    ]) {
      const b = ctx.button(label, () => {
        if (ctx.feedScope === value) return;
        ctx.feedScope = value;
        ctx.feed.reset(ctx.mode);
        ctx.feedScroll = 0;
        ctx.render();
      });
      b.prepend(ctx.icon(symbol));
      b.setAttribute('aria-pressed', String(ctx.feedScope === value));
      lanes.append(b);
    }
    const toolbar = ctx.e('div', 'feed-toolbar');
    const actions = ctx.e('div', 'feed-toolbar-actions');
    const latest = ctx.button('Latest', () => selectOrder('latest'), 'order-button');
    latest.prepend(ctx.icon('clock'));
    latest.setAttribute('aria-label', ctx.uiText('Latest'));
    latest.title = ctx.uiText('Latest');
    latest.setAttribute('aria-pressed', String(ctx.mode === 'latest'));
    actions.append(latest, ctx.iconButton('refresh-cw', 'Refresh feed', ctx.refreshFeed));
    if (ctx.featureEnabled('publishing'))
      actions.append(ctx.iconButton('plus', 'Share a moment', () => ctx.composer()));
    const extras = ctx.e('details', 'feed-extras');
    const extraToggle = ctx.append(ctx.e('summary', 'icon-button'), ctx.icon('image'));
    extraToggle.setAttribute('aria-label', ctx.uiText('Stories & reels'));
    extraToggle.title = ctx.uiText('Stories & reels');
    extras.append(extraToggle);
    if (ctx.featureEnabled('stories')) {
      const storySlot = ctx.e('div');
      extras.append(storySlot);
      extras.addEventListener('toggle', async () => {
        if (!extras.open || storySlot.childElementCount) return;
        const loading = ctx.e('p', 'feed-loading', ctx.uiText('Loading…'));
        storySlot.append(loading);
        try {
          if (ctx.ensureFullState && !(await ctx.ensureFullState())) {
            loading.remove();
            return;
          }
          if (storySlot.isConnected) storySlot.replaceChildren(stories());
        } catch (error) {
          if (!storySlot.isConnected) return;
          storySlot.replaceChildren(
            ctx.button(
              'Try again',
              () => {
                storySlot.replaceChildren();
                extras.dispatchEvent(new Event('toggle'));
              },
              'secondary-button',
            ),
          );
          ctx.toast(error.message);
        }
      });
    }
    if (ctx.featureEnabled('reels'))
      extras.append(
        ctx.append(
          ctx.e('div', 'button-row'),
          ctx.button('Make a reel', () => ctx.composer('reel')),
          ctx.link('Watch reels', '#/reels', 'secondary-button'),
        ),
      );
    toolbar.append(lanes, actions);
    if (
      ctx.contentScope !== 'learning' &&
      (ctx.featureEnabled('stories') || ctx.featureEnabled('reels'))
    )
      toolbar.append(extras);
    ctx.view.dataset.experience = ctx.experience;
    ctx.append(
      ctx.view,
      pullIndicator,
      ctx.e(
        'h1',
        'sr-only',
        ctx.uiText(
          ctx.experience === 'visual' ? 'Visual' : ctx.experience === 'talk' ? 'Talk' : 'Wall',
        ),
      ),
      toolbar,
      list,
      sentinel,
    );
    if (ctx.feed.started) {
      paintFeedItems();
      setupPaging();
    } else {
      list.append(feedLoading(ctx));
      loadNext();
    }
  }

  function caughtUpMarker() {
    return feedBoundaryMarker(ctx);
  }

  function setupPaging() {
    alignWarmup();

    ctx.feedObserver?.disconnect();
    const sentinel = document.querySelector('#feed-sentinel');
    if (!sentinel) return;
    sentinel.replaceChildren();
    if (!ctx.feed.cursor) {
      if (
        ctx.feed.started &&
        ctx.feed.cursor === null &&
        ctx.feedScope === 'following' &&
        ctx.mode !== 'latest'
      ) {
        sentinel.append(
          ctx.e(
            'h2',
            'following-discovery-boundary',
            ctx.uiText('You’re caught up with Following'),
          ),
        );
        recommendations.mount(sentinel, {
          context: 'following',
          title: 'More to explore',
        });
      }
      sentinel.append(
        ctx.e(
          'p',
          'subtle',
          ctx.contentScope === 'learning'
            ? ctx.feed.items.length
              ? 'You’ve reached the end of these learning readings.'
              : 'No learning readings match this view yet. Try For you or browse the guides.'
            : 'That’s every moment in this feed.',
        ),
      );
      if (ctx.contentScope === 'learning')
        sentinel.append(ctx.link('Browse learning guides', '#/courses', 'secondary-button'));
      if (ctx.feedScope === 'following' && !ctx.state.following.length)
        sentinel.append(
          ctx.e('p', 'subtle', 'Follow someone to bring their public moments here.'),
          ctx.link('Find people to follow', '#/discover', 'secondary-button'),
        );
      return;
    }
    sentinel.setAttribute('role', 'status');
    sentinel.append(
      ctx.e(
        'span',
        'sr-only',
        ctx.uiText(
          ctx.contentScope === 'learning' ? 'Loading learning readings…' : 'Loading your moments…',
        ),
      ),
    );
    ctx.feedObserver = observeContinuation(sentinel, loadNext, {
      active: () => ['feed', 'learning'].includes(ctx.route()[0]) && !!ctx.state,
    });
    if (!navigator.connection?.saveData && !/2g/.test(navigator.connection?.effectiveType ?? ''))
      ctx.feed
        .prefetch()
        .then((page) => {
          for (const item of page?.items.slice(0, 2) ?? []) {
            if (item.post.image?.startsWith('/assets/')) {
              const img = new Image();
              img.decoding = 'async';
              img.fetchPriority = 'low';
              img.src = ctx.photoSource(item.post.image, 160);
            }
          }
        })
        .then(() =>
          warming.warm(
            warmup,
            ctx.experience,
            ['wall', 'visual', 'talk'].filter(
              (value) => value === 'wall' || ctx.state.features?.[`${value}-feed`] !== false,
            ),
          ),
        )
        .catch(() => {});
  }

  function loadNext() {
    if (ctx.pagingTask) return ctx.pagingTask;
    const generation = ctx.feed.generation;
    ctx.pagingTask = loadNextImpl().finally(() => {
      ctx.pagingTask = null;
      if (
        generation !== ctx.feed.generation &&
        ctx.state &&
        ['feed', 'learning'].includes(ctx.route()[0])
      ) {
        if (ctx.feed.started) setupPaging();
        else queueMicrotask(loadNext);
      }
    });
    return ctx.pagingTask;
  }

  async function resumeReading() {
    const anchor = ctx.state.settings.reading?.anchor;
    if (!anchor) return;
    await loadNext();
    while (ctx.feed.cursor && !ctx.feed.items.some((item) => item.id === anchor.id)) {
      const before = ctx.feed.cursor;
      await loadNext();
      if (before === ctx.feed.cursor) {
        ctx.toast('Could not resume. Refresh the wall or retry loading.');
        return;
      }
    }
    const card = [...ctx.view.querySelectorAll('.post')].find(
      (c) => c.dataset.postId === anchor.id,
    );
    if (card) {
      card.scrollIntoView({ block: 'start', behavior: 'instant' });
      window.scrollBy({
        top: -Math.max(80, anchor.offset),
        behavior: 'instant',
      });
    } else ctx.toast('That moment is no longer available. Your new moments are here.');
  }

  async function loadNextImpl() {
    if (ctx.paging || !['feed', 'learning'].includes(ctx.route()[0]) || !ctx.state) return;
    ctx.paging = true;
    const generation = ctx.feed.generation,
      actor = ctx.state.me.id;
    const sentinel = document.querySelector('#feed-sentinel');
    if (sentinel) {
      sentinel.setAttribute('aria-busy', 'true');
      sentinel.querySelector('button')?.setAttribute('disabled', '');
    }
    try {
      const first = !ctx.feed.started;
      const requestDone = ctx.api.timings?.start(
        first ? 'first-screen-request' : 'load-more',
        ctx.experience,
      );
      const imageDone = first
        ? ctx.api.timings?.start('first-image-ready', ctx.experience)
        : undefined;
      const ready = first
        ? ctx.api.timings?.start('first-screen-ready', ctx.experience)
        : undefined;
      const added = await ctx.feed.next();
      requestDone?.();
      if (
        generation !== ctx.feed.generation ||
        ctx.state?.me.id !== actor ||
        !['feed', 'learning'].includes(ctx.route()[0])
      )
        return;
      for (const user of ctx.feed.meta?.users ?? []) {
        const index = ctx.state.users.findIndex((current) => current.id === user.id);
        if (index < 0) ctx.state.users.push(user);
        else ctx.state.users[index] = user;
      }
      for (const item of added) {
        const index = ctx.state.posts.findIndex((p) => p.id === item.id);
        if (index < 0) ctx.state.posts.push(item.post);
        else ctx.state.posts[index] = item.post;
      }
      const list = document.querySelector('#feed-list');
      list?.querySelector('.feed-loading')?.remove();
      if (list) {
        // One reconciliation owner applies cadence on first load and every page,
        // retaining existing private rounds while rechecking current post access.
        paintFeedItems();
      }
      ctx.api.timings?.afterPaint(ready ?? (() => {}));
      if (first)
        ctx.api.timings?.afterImage(
          imageDone ?? (() => {}),
          list?.querySelector('.post-photo, .media-photo-button img'),
        );
      setupPaging();
    } catch (error) {
      if (generation === ctx.feed.generation && ['feed', 'learning'].includes(ctx.route()[0])) {
        document.querySelector('#feed-list .feed-loading')?.remove();
        sentinel?.replaceChildren(
          ctx.e('p', 'form-error', error.message),
          ctx.button(
            [400, 410].includes(error.status) ? 'Refresh feed' : 'Retry loading',
            [400, 410].includes(error.status) ? ctx.refreshFeed : loadNext,
          ),
        );
      }
    } finally {
      ctx.paging = false;
      sentinel?.removeAttribute('aria-busy');
    }
  }
  return {
    modeDescription,
    updatePreferences,
    headerPreferences,
    controls,
    stories,
    storyDialog,
    chooseExperience,
    modeWheel,
    renderFeed,
    canReuseFeedSurface,
    disposeFeedWarmup,
    paintFeedItems,
    caughtUpMarker,
    setupPaging,
    loadNext,
    resumeReading,
    loadNextImpl,
  };
}
